/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BladeModel } from 'src/core/components/BladeModel';
import { LuisText } from 'src/core/components/LuisText';
import { StackBladeHost } from 'src/core/components/StackBladeHost';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist, important } from 'src/core/themes/luisStylist';
import { BatchBlade } from 'src/luis/projects/testPane/components/BatchBlade/BatchBlade';
import { ChatBlade } from 'src/luis/projects/testPane/components/ChatBlade/ChatBlade';
import { TestBladeHeader } from 'src/luis/projects/testPane/components/common/TestBladeHeader';
import { DebugBlade } from 'src/luis/projects/testPane/components/DebugBlade';
import { EndpointDebugBlade } from 'src/luis/projects/testPane/components/EndpointBlade/EndpointDebugBlade';
import { PublishSlotType } from 'src/luis/types/PublishSlotType';

const messages = defineMessages({
    testButton: {
        id: 'testPane.testPane.test',
        defaultMessage: 'Test'
    },
    currentVersion: {
        id: 'testPane.testPane.currentVersion',
        defaultMessage: 'Version: { versionId }'
    },
    publishedVersion: {
        id: 'testPane.testPane.publishedVersion',
        defaultMessage: 'Published'
    },
    batchTesting: {
        id: 'testPane.testPane.batchTesting',
        defaultMessage: 'Batch testing'
    }
});

const { getClassName: getDebugBladeClasses } = getStylist('DebugBlade');
const { getClassName: getEndpointBladeClasses } = getStylist('EndpointBlade');

const debugBladeBackground = getDebugBladeClasses('DebugBladeBackground', theme => ({
    color: theme.textColor,
    backgroundColor: theme.backgroundColor
}));

const endpointBladeBackground = getEndpointBladeClasses('EndpointBladeBackground', theme => ({
    color: theme.textColor,
    backgroundColor: theme.backgroundColor
}));
const batchBladeTheme = getScopedTheme('BatchBlade');
const endpointBladeTheme = getScopedTheme('EndpointBlade');

const slotStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({
    root: { textTransform: 'capitalize', fontStyle: 'italic', color: endpointBladeTheme.secondaryTextColor }
});

/**
 * When a modal or panel is open, Office Fabric adds a hidden
 * span with class name of ms-layer. This span is considered an element
 * inside the Stack, and so a margin for it is calculated. This class
 * ensures that the div wrapper of the palette has a margin of 0, and
 * thus when the span element is added inside of it, it is not affected
 * by the stack margining.
 */
const nudgeFixWrapper = getDebugBladeClasses('NudgeFixWrapper', important({ marginLeft: 0 }));

type BladeId = 'chatBlade' | 'debugBlade' | 'endpointDebugBlade' | 'batchBlade';

/**
 * Renders the test pane and its blades.
 */
export const TestPane = observer(() => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [bladeIds, setBladeIds] = React.useState<BladeId[]>([]);
    const [currentTestingSlot, setCurrentTestingSlot] = React.useState<PublishSlotType>('staging');
    const [selectedUtteranceId, setSelectedUtteranceId] = React.useState<React.ReactText>('');
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const versionStore = rootStore.currentLuisAppStore.currentAppVersionStore;
    const isTestingDisabled = !versionStore.isTrained || versionStore.trainingStore.lastTrainingMode === 'Neural';

    React.useEffect(() => (bladeIds.length ? setIsOpen(true) : setIsOpen(false)), [bladeIds]);

    /**
     * Adds a blade to the end of the currently
     * open blades.
     *
     * @param bladeId The blade id to add.
     */
    const pushBlade = (bladeId: BladeId) => {
        if (bladeIds.indexOf(bladeId) < 0) {
            setBladeIds([...bladeIds, bladeId]);
        }
    };

    /**
     * Removes the most recently added blade.
     */
    const popBlade = () => setBladeIds(bladeIds.slice(0, bladeIds.length - 1));

    const blades = bladeIds.map<BladeModel>(bladeId => {
        switch (bladeId) {
            case 'debugBlade':
                return {
                    key: bladeId,
                    header: <TestBladeHeader>{localize(messages.currentVersion, { versionId: versionStore.id })}</TestBladeHeader>,
                    content: (
                        <DebugBlade
                            onStartOver={() => setBladeIds(['chatBlade'])}
                            selectedUtteranceId={selectedUtteranceId}
                            onComparePublished={() => pushBlade('endpointDebugBlade')}
                            bladeHeader={localize(messages.currentVersion, { versionId: versionStore.id })}
                        />
                    ),
                    className: debugBladeBackground,
                    isDarkTheme: true
                };
            case 'endpointDebugBlade':
                const isPublished = Boolean(rootStore.currentLuisAppStore.publishSlots.production)
                    || Boolean(rootStore.currentLuisAppStore.publishSlots.staging);

                return {
                    key: bladeId,
                    header: (
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                            <TestBladeHeader>{localize(messages.publishedVersion)}</TestBladeHeader>
                            {isPublished && <LuisText styles={slotStyles}>({currentTestingSlot})</LuisText>}
                        </Stack>
                    ),
                    content: (
                        <EndpointDebugBlade
                            publishSlotToUse={currentTestingSlot}
                            selectedUtteranceId={selectedUtteranceId}
                            onPublishSlotToUseChange={setCurrentTestingSlot}
                            bladeHeader={localize(messages.publishedVersion)}
                        />
                    ),
                    className: endpointBladeBackground,
                    isDarkTheme: true
                };
            case 'batchBlade':
                return {
                    key: bladeId,
                    header: <TestBladeHeader>{localize(messages.batchTesting)}</TestBladeHeader>,
                    content: (
                        <BatchBlade
                            onOpenSingleTestingPanel={() => setBladeIds(['chatBlade'])}
                            bladeHeader={localize(messages.batchTesting)}
                        />
                    ),
                    width: batchBladeTheme.width
                };
            case 'chatBlade':
            default:
                return {
                    key: bladeId,
                    header: <TestBladeHeader>{localize(messages.testButton)}</TestBladeHeader>,
                    content: (
                        <ChatBlade
                            selectedUtteranceId={selectedUtteranceId}
                            onSelectUtterance={setSelectedUtteranceId}
                            onInspectResult={() => pushBlade('debugBlade')}
                            onOpenBatchTestingPanel={() => setBladeIds(['batchBlade'])}
                            onStartOver={() => setBladeIds(['chatBlade'])}
                        />
                    )
                };
        }
    });

    return (
        <>
            <DefaultButton
                disabled={isTestingDisabled}
                data-automation-id="TestButton"
                iconProps={{ iconName: 'TestBeaker' }}
                onClick={() => setBladeIds(['chatBlade'])}>
                {localize(messages.testButton)}
            </DefaultButton>

            <div className={nudgeFixWrapper}>
                <StackBladeHost isOpen={isOpen} blades={blades} onLastBladeDismissed={popBlade} onDismiss={popBlade} />
            </div>
        </>
    );
});
